import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Theaters as ShowTimeIcon } from '@mui/icons-material';
import io from 'socket.io-client';
import { useAuthContext } from '../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const POLL_MS = 10000;

const ShowTimeLobby = () => {
  const { showTimeId } = useParams();
  const [loading, setLoading] = useState(true);
  const [, setAdmitted] = useState(false);
  const [showName, setShowName] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthContext();
  const socketRef = useRef(null);

  const checkAdmission = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE}/api/show-times/${showTimeId}/check-admission`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.admitted) {
      setAdmitted(true);
      setShowName(data.showTime?.name || '');
      navigate('/mindyourmusic', { replace: true });
    } else if (data.participant?.status === 'rejected') {
      setError('You were rejected from this show.');
    } else {
      setShowName(data.showTime?.name || '');
    }
    return data;
  }, [showTimeId, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { from: `/showtime/lobby/${showTimeId}` } });
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const data = await checkAdmission();
        if (!cancelled && data.admitted) return;
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to check status');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();

    const socket = io(API_BASE, { transports: ['websocket'], reconnection: true });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('showtime:join-lobby', { showTimeId: Number(showTimeId) });
    });

    socket.on('showtime:admission-update', ({ userId, status }) => {
      if (!cancelled && user && String(userId) === String(user.id)) {
        if (status === 'admitted') {
          setAdmitted(true);
          navigate('/mindyourmusic', { replace: true });
        } else if (status === 'rejected') {
          setError('You were rejected from this show.');
        }
      }
    });

    const interval = setInterval(run, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (socketRef.current) {
        socketRef.current.emit('showtime:leave-lobby', { showTimeId: Number(showTimeId) });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, user, navigate, showTimeId, checkAdmission]);

  if (!isAuthenticated || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#FF6B9D' }} />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', color: '#fff' }}>
        <CircularProgress sx={{ color: '#FF6B9D', mb: 2 }} />
        <Typography>Checking admission...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 500,
          width: '100%',
          p: 4,
          backgroundColor: 'rgba(30,30,50,0.95)',
          border: '2px solid rgba(255,107,157,0.4)',
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <ShowTimeIcon sx={{ fontSize: 64, color: '#FF6B9D', mb: 2 }} />
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
          {showName || 'Show Lobby'}
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <>
            <Typography variant="body1" sx={{ color: '#B0B0B0', mb: 2 }}>
              Waiting for admin to admit you...
            </Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>
              You will be redirected to Mind Your Music when admitted.
            </Typography>
            <CircularProgress sx={{ color: '#FF6B9D', mt: 3 }} size={32} />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ShowTimeLobby;
